import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Edit2,
  RefreshCw,
  AlertCircle,
  X,
  Layers,
  Check,
  XCircle,
  Shield,
  Clock,
} from 'lucide-react';
import {
  workflows,
  AdminWorkflow,
  CreateWorkflowRequest,
} from '../../api/client';
import { cn } from '../../lib/utils';

export default function Workflows() {
  const navigate = useNavigate();
  const [workflowList, setWorkflowList] = useState<AdminWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const res = await workflows.list();
      setWorkflowList(res.workflows);
      setError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch workflows';
      console.error('Failed to fetch workflows:', error);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleCreateWorkflow = async (data: CreateWorkflowRequest) => {
    try {
      setError(null);
      await workflows.create(data);
      await fetchData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create workflow';
      console.error('Failed to create workflow:', error);
      setError(message);
      throw new Error(message);
    }
  };

  const handleToggleEnabled = async (workflow: AdminWorkflow) => {
    try {
      await workflows.update(workflow.id, { is_enabled: !workflow.isEnabled });
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle workflow:', error);
    }
  };

  const handleDeleteWorkflow = async (workflow: AdminWorkflow) => {
    if (!confirm(`Are you sure you want to delete workflow "${workflow.name}"?`)) {
      return;
    }

    try {
      await workflows.delete(workflow.id);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete workflow:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading workflows...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient-violet">Workflows</h1>
          <p className="text-muted-foreground mt-1">Manage ComfyUI workflow templates</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-glow-violet px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
        >
          <span className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Workflow
          </span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-rose-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-rose-400 mb-1">Error</p>
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="p-2 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Workflows Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {workflowList.map((workflow, index) => (
          <WorkflowCard
            key={workflow.id}
            workflow={workflow}
            delay={index * 100}
            onEdit={() => navigate(`/image/workflows/${workflow.id}`)}
            onToggle={() => handleToggleEnabled(workflow)}
            onDelete={() => handleDeleteWorkflow(workflow)}
          />
        ))}
        {workflowList.length === 0 && (
          <div className="col-span-2 card-glow rounded-2xl p-16">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                <Layers className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-muted-foreground mb-1">No workflows configured</p>
                <p className="text-sm text-muted-foreground/70">Add a workflow to define image generation templates</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-secondary px-4 py-2 rounded-lg text-sm"
              >
                Add your first workflow
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Workflow Modal */}
      {showCreateModal && (
        <CreateWorkflowModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateWorkflow}
        />
      )}
    </div>
  );
}

function WorkflowCard({
  workflow,
  delay,
  onEdit,
  onToggle,
  onDelete,
}: {
  workflow: AdminWorkflow;
  delay: number;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const enabledBg = workflow.isEnabled ? 'bg-emerald-500/10' : 'bg-gray-500/10';
  const icon = workflow.icon || '\u26A1';

  return (
    <div
      className="card-glow rounded-2xl p-6 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center text-2xl',
            enabledBg
          )}>
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-lg">{workflow.name}</h3>
            <code className="text-xs font-mono text-muted-foreground">{workflow.slug}</code>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-violet-400 transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          {!workflow.isPermanent && (
            <button
              onClick={onDelete}
              className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-rose-400 transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Description */}
      {workflow.description && (
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {workflow.description}
        </p>
      )}

      {/* Status Badges */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className={cn(
          'px-2 py-0.5 text-xs font-medium rounded-md',
          workflow.isEnabled
            ? 'bg-emerald-500/15 text-emerald-400'
            : 'bg-gray-500/15 text-gray-400'
        )}>
          {workflow.isEnabled ? 'Enabled' : 'Disabled'}
        </span>

        <span className={cn(
          'px-2 py-0.5 text-xs font-medium rounded-md flex items-center gap-1',
          workflow.isConfigured
            ? 'bg-emerald-500/15 text-emerald-400'
            : 'bg-rose-500/15 text-rose-400'
        )}>
          {workflow.isConfigured ? (
            <>
              <Check className="w-3 h-3" />
              Configured
            </>
          ) : (
            <>
              <XCircle className="w-3 h-3" />
              Not Configured
            </>
          )}
        </span>

        {workflow.isPermanent && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-amber-500/15 text-amber-400 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Permanent
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border/50">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          <span>Created {new Date(workflow.createdAt).toLocaleDateString()}</span>
        </div>
        <button
          onClick={onToggle}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-lg transition-all',
            workflow.isEnabled
              ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
              : 'bg-secondary text-muted-foreground hover:text-foreground'
          )}
        >
          {workflow.isEnabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>
    </div>
  );
}

function CreateWorkflowModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (data: CreateWorkflowRequest) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateSlug = (value: string): string => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugManuallyEdited) {
      setSlug(generateSlug(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const data: CreateWorkflowRequest = {
      name,
      slug,
    };
    if (description.trim()) data.description = description.trim();
    if (icon.trim()) data.icon = icon.trim();

    try {
      setSubmitting(true);
      setError(null);
      await onSubmit(data);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create workflow';
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4">
      <div
        className="modal-content rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
            <Layers className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Add Workflow</h2>
            <p className="text-sm text-muted-foreground">
              Create a new ComfyUI workflow template
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Name <span className="text-rose-400">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm"
              placeholder="Realistic Portrait"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Slug <span className="text-rose-400">*</span>
            </label>
            <input
              type="text"
              required
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              placeholder="realistic-portrait"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              URL-safe identifier. Auto-generated from name.
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm resize-none"
              rows={3}
              placeholder="A workflow for generating realistic portraits..."
            />
          </div>

          {/* Icon */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Icon (emoji)
            </label>
            <input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm"
              placeholder="\u26A1"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Emoji icon displayed on the workflow card.
            </p>
          </div>

          {error && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 animate-scale-in">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-rose-300">{error}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={cn(
                'btn-secondary px-5 py-2.5 rounded-xl text-sm',
                submitting && 'opacity-50 cursor-not-allowed'
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim() || !slug.trim()}
              className={cn(
                'btn-glow-violet px-5 py-2.5 rounded-xl text-sm flex items-center gap-2',
                (submitting || !name.trim() || !slug.trim()) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {submitting && <RefreshCw className="w-4 h-4 animate-spin" />}
              <span>{submitting ? 'Creating...' : 'Create Workflow'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
